import { skipToken } from "@apollo/client";
import type { SuspenseQueryHookOptions } from "@apollo/client";
import { useMutationHandler } from "@sinch/hooks/useMutationHandler";
import { useSuspenseQueryHandler } from "@sinch/hooks/useSuspenseQueryHandler";
import {
  AddRateComponent,
  DeleteRateComponent,
  EditRateComponent,
  RateComponent,
} from "@sinch/components/application/RateComponent/queries.graphql";
import type {
  AddRateComponentMutation,
  AddRateComponentMutationVariables,
  DeleteRateComponentMutation,
  DeleteRateComponentMutationVariables,
  EditRateComponentMutation,
  EditRateComponentMutationVariables,
  RateComponentQuery,
  RateComponentQueryVariables,
} from "@sinch/components/application/RateComponent/queries.types";

export const useRateComponent = (props?: {
  id?: string;
  queryOptions?: Omit<SuspenseQueryHookOptions<RateComponentQuery, RateComponentQueryVariables>, "variables">;
}) => {
  const [deleteComponent, { loading: deleteLoading }] = useMutationHandler<
    DeleteRateComponentMutation,
    DeleteRateComponentMutationVariables
  >(DeleteRateComponent);

  const [createComponent, { loading: createLoading }] = useMutationHandler<
    AddRateComponentMutation,
    AddRateComponentMutationVariables
  >(AddRateComponent);

  const [editComponent, { loading: editLoading }] = useMutationHandler<
    EditRateComponentMutation,
    EditRateComponentMutationVariables
  >(EditRateComponent);

  const componentId = props?.id;
  const queryVariables = componentId ? { id: componentId } : undefined;

  const { data } = useSuspenseQueryHandler<RateComponentQuery, RateComponentQueryVariables>(
    RateComponent,
    queryVariables,
    componentId ? (props.queryOptions ?? {}) : skipToken
  );

  return {
    create: createComponent,
    edit: editComponent,
    remove: deleteComponent,
    loading: deleteLoading || editLoading || createLoading,
    rateComponent: data,
  };
};
